import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import {
  IntroText,
  CheckboxList,
  FormActionDrawer,
  Loader
} from "../../../components";
import { useHeader } from "../../../contexts";
import { GET_AVAILABLE_TIMES } from "../../../queries";
import { useTranslation } from "react-i18next";

const useClasses = makeStyles(theme => ({
  accordionDetails: {
    display: "block"
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default
  }
}));

/**
 * Groups an array of time objects into 1 hour time slots
 *
 * @param {*} times
 */
const groupTimesByHour = times => {
  let groupedByHour = {};

  times.forEach(slot => {
    const [time, period] = slot.label.split(" ");
    const [hour] = time.split(":");

    let toHour = parseInt(hour) + 1 > 12 ? 1 : parseInt(hour) + 1;

    let groupLabel = `${parseInt(hour)}:00 ${period} - ${toHour}:00 ${
      parseInt(hour) + 1 === 12 ? (period === "AM" ? "PM" : "AM") : period
    }`;

    groupedByHour[groupLabel] = groupedByHour[groupLabel]
      ? [...groupedByHour[groupLabel], slot]
      : [slot];
  });

  return groupedByHour;
};

const ScheduleTime = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData = {},
  isSubmitting
}) => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { setTitle } = useHeader();
  const [timeSlot, setTimeSlot] = useState();
  const [error, setError] = useState(false);

  useEffect(() => {
    setTitle(t("schedule.time.title"));
  }, []);

  const { data, loading, error: getTimesError } = useQuery(
    GET_AVAILABLE_TIMES,
    {
      variables: {
        location: scheduleData.location,
        date: scheduleData.date
      },
      fetchPolicy: "no-cache"
    }
  );

  const handleChange = values => {
    setTimeSlot(values);
  };

  const handleNext = () => {
    if (timeSlot) {
      updateScheduleData({ timeSlot });
    } else {
      setError(true);
    }
  };

  if (loading || isSubmitting) return <Loader />;
  if (getTimesError)
    return (
      <Typography color="error">{t("schedule.time.error_api")}</Typography>
    );

  const { availableTimes } = data;

  const timesGrouped =
    availableTimes.available_times && availableTimes.available_times.length > 0
      ? groupTimesByHour(availableTimes.available_times)
      : undefined;

  return (
    <Box data-cy="schedule-screen-time">
      <IntroText>{t("schedule.time.intro_text")}</IntroText>
      {error && (
        <Typography data-cy="schedule-screen-time-error" color="error" pb={2}>
          {t("schedule.time.error")}
        </Typography>
      )}
      {timesGrouped ? (
        <>
          {Object.keys(timesGrouped).map((key, index) => (
            <Accordion key={`accordion-${key}`}>
              <AccordionSummary
                className={classes.accordionSummary}
                expandIcon={<ExpandMore />}
                aria-controls={`panel-${index}`}
                id={`panel-${index}`}
              >
                <Typography>{key}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <CheckboxList
                  options={timesGrouped[key]}
                  onChange={handleChange}
                  initialValues={scheduleData.time}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      ) : (
        <Typography paragraph>{t("schedule.time.error_empty")}</Typography>
      )}
      <FormActionDrawer
        setDrawerClearance={setDrawerClearance}
        handleNext={e => handleNext()}
        loading={isSubmitting}
      />
    </Box>
  );
};

export { ScheduleTime };
